import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "services/config";
import { useStore } from "context/store/store";
import { formatCLP, months } from "utils";
import {
  TrendingUp,
  Today,
  CalendarMonth,
  People,
  PersonAdd,
  Warning,
} from "@mui/icons-material";
import { Skeleton } from "@mui/material";

const CARD_STYLES = {
  base: {
    background: "#fff",
    borderRadius: 12,
    padding: "18px 20px",
    minWidth: 0,
    position: "relative",
    overflow: "hidden",
    border: "1px solid #e8ecf1",
  },
};

function StatCard({ label, value, subtitle, icon: Icon, accent = "#0353a4", loading }) {
  return (
    <div style={CARD_STYLES.base}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 4,
          height: "100%",
          background: accent,
          borderRadius: "12px 0 0 12px",
        }}
      />
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#8b95a5",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              margin: 0,
              marginBottom: 6,
            }}
          >
            {label}
          </p>
          {loading ? (
            <Skeleton variant="text" width={100} sx={{ fontSize: "1.4rem" }} />
          ) : (
            <p
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#0f172a",
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              {value ?? "--"}
            </p>
          )}
          {subtitle && (
            <p style={{ fontSize: 11, color: "#94a3b8", margin: 0, marginTop: 4 }}>{subtitle}</p>
          )}
        </div>
        {Icon && (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: `${accent}12`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginLeft: 12,
            }}
          >
            <Icon style={{ fontSize: 18, color: accent }} />
          </div>
        )}
      </div>
    </div>
  );
}

function DetailPopover({ items, open }) {
  if (!open || !items || items.length === 0) return null;
  return (
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        marginTop: 4,
        background: "#0f172a",
        borderRadius: 10,
        padding: "12px 14px",
        zIndex: 50,
        boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          paddingBottom: 6,
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>
          Tipo
        </span>
        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>
          % - Cant.
        </span>
      </div>
      {items.map((d, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "3px 0",
          }}
        >
          <span style={{ fontSize: 11, color: "#e2e8f0" }}>{d.type}</span>
          <span style={{ fontSize: 11, color: "#cbd5e1" }}>
            {d.percentage}% - {d.count}
          </span>
        </div>
      ))}
    </div>
  );
}

const MONTH_PILL = {
  base: {
    padding: "4px 10px",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 500,
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#64748b",
    cursor: "pointer",
    transition: "all 0.15s",
  },
  active: {
    background: "#0353a4",
    color: "#fff",
    border: "1px solid #0353a4",
  },
};

const SHORT_MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function HeaderStats() {
  const store = useStore();
  const r = store.reports;
  const [showActiveDetail, setShowActiveDetail] = useState(false);
  const [showEnrollDetail, setShowEnrollDetail] = useState(false);

  const now = new Date();
  const [selMonth, setSelMonth] = useState(now.getMonth());
  const [selYear, setSelYear] = useState(now.getFullYear());
  const isCurrentMonth = selMonth === now.getMonth() && selYear === now.getFullYear();

  const [monthSales, setMonthSales] = useState(null);
  const [monthBussines, setMonthBussines] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (isCurrentMonth) {
      setMonthSales(null);
      setMonthBussines(null);
      return;
    }
    let cancelled = false;
    setFetching(true);
    Promise.all([
      axios.get(`${config.url}/reports/monthly-sales?month=${selMonth}&year=${selYear}`),
      axios.get(`${config.url}/reports/bussines_compare?month=${selMonth}&year=${selYear}`),
    ]).then(([salesRes, bussinesRes]) => {
      if (cancelled) return;
      setMonthSales(salesRes.data.monthlySales.totalSales);
      setMonthBussines(bussinesRes.data);
      setFetching(false);
    }).catch(() => {
      if (!cancelled) setFetching(false);
    });
    return () => { cancelled = true; };
  }, [selMonth, selYear, isCurrentMonth]);

  const displayMonthlySales = isCurrentMonth ? r.monthlySales : monthSales;
  const displayBussines = isCurrentMonth ? r.bussines : monthBussines;
  const loading = r.monthlySales === null && r.totalToday === null;
  const monthLoading = !isCurrentMonth && fetching;

  return (
    <div style={{ padding: "20px 32px 12px" }}>
      {/* Month selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        <select
          value={selYear}
          onChange={(e) => setSelYear(parseInt(e.target.value))}
          style={{
            padding: "4px 8px",
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            border: "1px solid #e2e8f0",
            background: "#fff",
            color: "#0f172a",
            cursor: "pointer",
            marginRight: 4,
          }}
        >
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {SHORT_MONTHS.map((m, i) => (
          <div
            key={i}
            onClick={() => setSelMonth(i)}
            style={{
              ...MONTH_PILL.base,
              ...(selMonth === i ? MONTH_PILL.active : {}),
            }}
          >
            {m}
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 12,
          maxWidth: 1600,
        }}
      >
        <StatCard
          label={isCurrentMonth ? "Este mes" : months[selMonth]}
          value={displayMonthlySales !== null ? formatCLP(displayMonthlySales) : null}
          subtitle={months[selMonth]}
          icon={CalendarMonth}
          accent="#0353a4"
          loading={loading || monthLoading}
        />
        <StatCard
          label="Hoy"
          value={r.totalToday !== null ? formatCLP(r.totalToday) : null}
          subtitle={now.toLocaleDateString()}
          icon={Today}
          accent="#16a34a"
          loading={loading}
        />
        <StatCard
          label="Membresias"
          value={displayBussines?.enrollmentPercent}
          subtitle={displayBussines?.totalEnrollmentAmount ? `${formatCLP(displayBussines.totalEnrollmentAmount)}` : null}
          icon={TrendingUp}
          accent="#d97706"
          loading={loading || monthLoading}
        />
        {/* Socios Activos - clickable */}
        <div style={{ position: "relative" }}>
          <div
            onClick={() => setShowActiveDetail(!showActiveDetail)}
            style={{ cursor: "pointer" }}
          >
            <StatCard
              label="Socios activos"
              value={r.memberkpis?.activeMembers?.toString()}
              icon={People}
              accent="#0d9488"
              loading={loading}
            />
          </div>
          <DetailPopover items={r.memberkpis?.activeType} open={showActiveDetail} />
        </div>

        {/* Inscripciones este mes - clickable */}
        <div style={{ position: "relative" }}>
          <div
            onClick={() => setShowEnrollDetail(!showEnrollDetail)}
            style={{ cursor: "pointer" }}
          >
            <StatCard
              label="Inscripciones este mes"
              value={r.memberkpis?.newEnrollmentsThisMonth?.toString()}
              subtitle={months[now.getMonth()]}
              icon={PersonAdd}
              accent="#4f46e5"
              loading={loading}
            />
          </div>
          <DetailPopover items={r.memberkpis?.thisMonthType} open={showEnrollDetail} />
        </div>

        <StatCard
          label="Inscripciones hoy"
          value={r.memberkpis?.newEnrollmentsToday?.toString()}
          subtitle={now.toLocaleDateString()}
          icon={PersonAdd}
          accent="#16a34a"
          loading={loading}
        />
        <StatCard
          label="Expiran este mes"
          value={r.memberkpis?.expiredMembersThisMonth?.toString()}
          icon={Warning}
          accent="#ef4444"
          loading={loading}
        />
      </div>
    </div>
  );
}
